import React, { useEffect, useState } from "react";
import { UserCheck, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Settings › My Coach card (clients only). Shows the linked coach, or lets
// the client link themselves with a coach code after signup.
export const MyCoachCard = () => {
  const { refresh } = useAuth();
  const [coach, setCoach] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/coaching/my_coach");
      setCoach(r.data);
    } catch {}
  };
  useEffect(() => {
    load();
  }, []);

  const link = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter your coach's code");
    setBusy(true);
    try {
      const r = await api.post("/coaching/link", { coach_code: code.trim() });
      const name = r.data?.coach?.first_name || r.data?.coach?.email;
      toast.success(`Linked — ${name} is now your coach`);
      setCode("");
      await Promise.all([refresh(), load()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm(`Unlink from ${coach?.first_name || coach?.email}? They will no longer see your progress or assign you programs.`)) return;
    try {
      await api.post("/coaching/unlink");
      toast.success("Unlinked from your coach");
      await Promise.all([refresh(), load()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlink failed");
    }
  };

  return (
    <div className="obs-card" data-testid="settings-my-coach-card">
      <div className="flex items-center gap-3 mb-4">
        <UserCheck className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">Coaching</div>
          <h3 className="font-heading text-2xl uppercase">My coach</h3>
        </div>
      </div>

      {coach ? (
        <>
          <div className="flex items-center justify-between gap-3 border border-obsidian-600 rounded-sm p-3 mb-3" data-testid="my-coach-linked">
            <div>
              <div className="text-sm text-zinc-100 font-medium">{coach.first_name || "Coach"}</div>
              <div className="text-xs text-zinc-500">{coach.email}</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-blaze border border-blaze/40 bg-blaze/10 px-2 py-1 rounded-full">Linked</span>
          </div>
          <button
            type="button"
            data-testid="my-coach-unlink-btn"
            onClick={unlink}
            className="inline-flex items-center gap-2 px-4 py-2 border border-obsidian-600 text-zinc-400 hover:text-blaze hover:border-blaze/50 uppercase tracking-widest text-xs rounded-sm transition-colors"
          >
            <Unlink className="h-3.5 w-3.5" /> Unlink coach
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-zinc-400 mb-3">
            Have a coach on Ripple Effect? Enter their code to link up — they'll see your
            progress, send you programs, and set your weekly focus.
          </p>
          <form onSubmit={link} className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="my-coach-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. Y4DGL8YX"
              maxLength={12}
              className="flex-1 bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm font-mono tracking-widest uppercase focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm"
            />
            <button
              type="submit"
              data-testid="my-coach-link-btn"
              disabled={busy || !code.trim()}
              className="blaze-btn px-5 py-2 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Link2 className="h-3.5 w-3.5" /> {busy ? "Linking…" : "Link coach"}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default MyCoachCard;
